import React from 'react';
import { toast } from 'react-toastify';
import { TERMINAL_STATUSES, TICKET_STATUSES, VOUCHER_STATUSES } from '../constants/enums';
import { defaultLocale, getTranslation } from './translation';
import {
    OUTCOME_STATUSES,
    MARKET_STATUSES,
    MATCH_DESCRIPTIONS,
    LANGUAGES,
    MINIMUM_ODD_VALUE,
} from '../constants/defaultValues';
import { convertToOrdinal } from './convertToOrdinal';
import { mbnColorPicker, sortOutcomeValues } from './helpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { roundNumber } from './formatDecimal';
import LocalStorage, { LOCAL_STORAGE_KEYS } from './localStorage';

export const showErrorNotification = message => {
    toast.error(message, {
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
    });
};

export const showSuccessNotification = message => {
    toast.success(message, {
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
    });
};

export const renderTicketStatusName = status => {
    switch (status) {
        case TICKET_STATUSES.open:
            return getTranslation('ticketStatusOpen');
        case TICKET_STATUSES.cancelled:
            return getTranslation('ticketStatusCancelled');
        case TICKET_STATUSES.won:
            return getTranslation('ticketStatusWon');
        case TICKET_STATUSES.lost:
            return getTranslation('ticketStatusLost');
        case TICKET_STATUSES.draw:
            return getTranslation('ticketStatusDraw');
        case TICKET_STATUSES.void:
            return getTranslation('ticketStatusVoid');
        case TICKET_STATUSES.halfLost:
            return getTranslation('ticketStatusHalfLost');
        case TICKET_STATUSES.halfWon:
            return getTranslation('ticketStatusHalfWon');
        case TICKET_STATUSES.refunded:
            return getTranslation('ticketStatusRefunded');
        case TICKET_STATUSES.paid:
            return getTranslation('ticketStatusPaid');
        case TICKET_STATUSES.cashOut:
            return getTranslation('ticketStatusCashOut');
        default:
            return '';
    }
};

export const renderVoucherStatusName = status => {
    switch (status) {
        case VOUCHER_STATUSES.active:
            return getTranslation('voucherStatusActive');
        case VOUCHER_STATUSES.inactive:
            return getTranslation('voucherStatusInactive');
        case VOUCHER_STATUSES.cancelled:
            return getTranslation('voucherStatusCancelled');
        default:
            return '';
    }
};

export const renderTerminalsStatusName = status => {
    switch (status) {
        case TERMINAL_STATUSES.active:
            return getTranslation('terminalStatusActive');
        case TERMINAL_STATUSES.inactive:
            return getTranslation('terminalStatusInactive');
        default:
            return '';
    }
};

export const getMatchDescription = (statusId, { type = 'default', locale } = {}) => {
    if (statusId === undefined || statusId === null) return '';

    const description = MATCH_DESCRIPTIONS[statusId.toString()];

    if (!description) return '';

    const { label, shortLabel } = description;

    if (type === 'short') {
        return shortLabel[locale];
    }

    return label[locale];
};

const parseScore = value => {
    const parts = value.split(':');

    if (parts.length !== 2) return null;

    if (!parts.every(part => /^\d+$/.test(part))) return null;

    return [Number(parts[0]), Number(parts[1])];
};

export const formatSpecifierName = ({ name, specifiers, teams, tournamentName, playerNames }) => {
    if (!name) return '';

    const patterns = findPatterns.get(name, '{', '}');

    let formattedName = name;

    const scoreSpecifier = specifiers.find(specifier => specifier.name === 'score');

    const specifierScores = scoreSpecifier !== undefined ? parseScore(scoreSpecifier.value) : null;
    const outcomeScores = specifierScores !== null ? parseScore(formattedName) : null;

    if (specifierScores !== null && outcomeScores !== null) {
        const [homeSpecifierScore, awaySpecifierScore] = specifierScores;
        const [homeOutcomeScore, awayOutcomeScore] = outcomeScores;

        formattedName = `${homeSpecifierScore + homeOutcomeScore}:${awaySpecifierScore + awayOutcomeScore}`;
    }

    patterns.forEach(pattern => {
        let replaceValue = '';

        if (pattern[0] === '!') {
            const specifierName = pattern.slice(1);
            const foundSpecifier = specifiers.find(specifier => specifier.name === specifierName);

            if (foundSpecifier) {
                replaceValue = convertToOrdinal(foundSpecifier.value);
            }
        } else if (pattern[0] === '+' || pattern[0] === '-') {
            const specifierName = pattern.slice(1);
            const foundSpecifier = specifiers.find(specifier => specifier.name === specifierName);
            if (foundSpecifier) {
                replaceValue =
                    pattern[0] === '+' ? foundSpecifier.value : foundSpecifier.value * -1;

                if (Number(replaceValue) > 0) {
                    replaceValue = `+${Math.abs(replaceValue)}`;
                } else if (Number(replaceValue) < 0) {
                    replaceValue = `-${Math.abs(replaceValue)}`;
                }
            }
        } else if (pattern.includes('+')) {
            let newPattern = pattern;
            let specifierValue = '';

            if (newPattern[0] === '!') {
                newPattern = newPattern.slice(1);
            }

            if (newPattern[0] === '(') {
                newPattern = newPattern.slice(1, newPattern.length - 1);
                const [specifierName, constant] = newPattern.split('+');
                const foundSpecifier = specifiers.find(
                    specifier => specifier.name === specifierName,
                );

                if (foundSpecifier) {
                    specifierValue = foundSpecifier.value + Number(constant);
                }
            }

            if (pattern[0] === '!') {
                replaceValue = convertToOrdinal(specifierValue);
            } else {
                replaceValue = specifierValue;
            }
        } else if (pattern.includes('-')) {
            let newPattern = pattern;
            let specifierValue = '';

            if (newPattern[0] === '!') {
                newPattern = newPattern.slice(1);
            }

            if (newPattern[0] === '(') {
                newPattern = newPattern.slice(1, newPattern.length - 1);
                const [specifierName, constant] = newPattern.split('+');
                const foundSpecifier = specifiers.find(
                    specifier => specifier.name === specifierName,
                );

                if (foundSpecifier) {
                    specifierValue = foundSpecifier.value - Number(constant);
                }
            }

            if (pattern[0] === '!') {
                replaceValue = convertToOrdinal(specifierValue);
            } else {
                replaceValue = specifierValue;
            }
        } else if (pattern.includes('$competitor')) {
            const competitorNumber = pattern[pattern.length - 1];

            if (competitorNumber === '1') {
                replaceValue = teams.home;
            } else if (competitorNumber === '2') {
                replaceValue = teams.away;
            }
        } else if (pattern.includes('$event')) {
            if (pattern.includes('tournament')) {
                replaceValue = tournamentName;
            } else if (pattern.includes('match')) {
                replaceValue = `${teams.home} vs ${teams.away}`;
            }
        } else if (pattern.includes('%player')) {
            const specifierValue = specifiers.find(specifier => specifier.name === 'player')?.value;
            replaceValue = playerNames[specifierValue];
        } else {
            const foundSpecifier = specifiers.find(specifier => specifier.name === pattern);

            if (formattedName.includes(`({${pattern}})`)) {
                replaceValue = foundSpecifier?.value ? `${foundSpecifier.value}` : '';
            } else {
                replaceValue = foundSpecifier?.value ? `(${foundSpecifier.value})` : '';
            }
        }

        formattedName = formattedName.replace(`{${pattern}}`, replaceValue);
    });

    return formattedName;
};

export const formatTeamNames = (teams, locale) => {
    return {
        home: teams.home?.[locale] || teams.home?.[LANGUAGES.eng],
        away: teams.away?.[locale] || teams.away?.[LANGUAGES.eng],
    };
};

const findPatterns = {
    results: [],
    string: '',
    getFromBetween: function (startingCharacter, endingCharacter) {
        if (this.string.indexOf(startingCharacter) < 0 || this.string.indexOf(endingCharacter) < 0)
            return false;
        var SP = this.string.indexOf(startingCharacter) + startingCharacter.length;
        var string1 = this.string.substr(0, SP);
        var string2 = this.string.substr(SP);
        var TP = string1.length + string2.indexOf(endingCharacter);
        return this.string.substring(SP, TP);
    },
    removeFromBetween: function (startingCharacter, endingCharacter) {
        if (this.string.indexOf(startingCharacter) < 0 || this.string.indexOf(endingCharacter) < 0)
            return false;
        var removal =
            startingCharacter +
            this.getFromBetween(startingCharacter, endingCharacter) +
            endingCharacter;
        this.string = this.string.replace(removal, '');
    },
    getAllResults: function (startingCharacter, endingCharacter) {
        // first check to see if we do have both substrings
        if (
            this.string.indexOf(startingCharacter) < 0 ||
            this.string.indexOf(startingCharacter) < 0
        )
            return;

        // find one result
        var result = this.getFromBetween(startingCharacter, endingCharacter);
        // push it to the results array
        this.results.push(result);
        // remove the most recently found one from the string
        this.removeFromBetween(startingCharacter, endingCharacter);

        // if there's more substrings
        if (
            this.string.indexOf(startingCharacter) > -1 &&
            this.string.indexOf(endingCharacter) > -1
        ) {
            this.getAllResults(startingCharacter, endingCharacter);
        } else return;
    },
    get: function (string, startingCharacter, endingCharacter) {
        this.results = [];
        this.string = string;
        this.getAllResults(startingCharacter, endingCharacter);
        return this.results;
    },
};

export const getMbnColor = mbn => mbnColorPicker(mbn);

export const findName = (names, locale) => {
    if (!names) return '';

    return names[locale] || names[LANGUAGES.eng] || '';
};

export const getStatisticsUrl = ({ eventId }) => {
    const WIDGET_LANGUAGES = {
        [LANGUAGES.eng]: 'en',
        [LANGUAGES.aze]: 'aze',
    };

    const userLanguage = LocalStorage.getItem(LOCAL_STORAGE_KEYS.lang) || defaultLocale;

    return `https://statshub.sportradar.com/caspiantechuof/${WIDGET_LANGUAGES[userLanguage]}/entity/${eventId}`;
};

export const formatMarkets = ({ markets, marketDescriptions, outcomeDescriptions }) => {
    return markets.map(market => {
        let marketNames;
        let specifierNames;
        let outcomeNames;

        if (market.dynamic) {
            marketNames = market.names || {};
            specifierNames = market.specifiers?.map(s => s.name) || [];
            if (market.outcomeNames) {
                if (!Array.isArray(market.outcomeNames)) {
                    outcomeNames =
                        Object.entries(market.outcomeNames).map(([key, value]) => {
                            return { id: key, names: value };
                        }) || [];
                } else {
                    outcomeNames = market.outcomeNames;
                }
            }
        } else {
            const marketDescription = marketDescriptions[market.marketRefId];
            const marketDescriptionOutcomes = marketDescription?.outcomes || [];

            marketNames = marketDescription?.names || {};
            specifierNames = marketDescription?.specifiers || [];
            outcomeNames = marketDescriptionOutcomes.map(outcome => {
                return outcomeDescriptions[outcome];
            });
        }

        return {
            ...market,
            names: marketNames,
            outcomeNames,
            specifierNames,
            outcomes: sortOutcomeValues(market.outcomes, 'outcomeRefId'),
        };
    });
};

export const renderOdd = ({ odd, marketStatus, outcomeStatus, isNull, isFrozen }) => {
    if (isNull) {
        return '-';
    } else if (marketStatus === MARKET_STATUSES.deactivated || isFrozen) {
        return (
            <span>
                <FontAwesomeIcon icon={faLock} />
            </span>
        );
    } else if (
        marketStatus === MARKET_STATUSES.suspended ||
        outcomeStatus === OUTCOME_STATUSES.closed
    ) {
        return '-';
    } else if (odd < MINIMUM_ODD_VALUE) {
        return '';
    } else {
        return roundNumber(odd, 2);
    }
};

export const formatTime = time => {
    let formattedTime = time?.split(':')[0] || '';

    if (formattedTime) formattedTime += " '";

    return formattedTime;
};
